import {
	Button,
	Group,
	Modal,
	Select,
	Stack,
	Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconArrowsSort } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { DataContainer, ScrollContainer } from '@codezo/ui';
import { WorkoutForm } from '../components/WorkoutForm';
import { WorkoutsList } from '../components/WorkoutsList';
import { WorkoutReorderModal } from '../components/WorkoutReorderModal';
import {
	useGetAllWorkoutsQuery,
	useCreateWorkoutMutation,
	useUpdateWorkoutMutation,
	useDeleteWorkoutMutation,
	useGetAllWorkoutCategoriesQuery,
	useBulkUpdateWorkoutSortOrderMutation,
} from '@codezo/graphql';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';

export function WorkoutsPage() {
	const [opened, { open, close }] = useDisclosure(false);
	const [reorderOpened, { open: openReorder, close: closeReorder }] = useDisclosure(false);
	const [selectedWorkout, setSelectedWorkout] = useState<any>(null);
	const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

	// GraphQL hooks
	const { data, loading, refetch } = useGetAllWorkoutsQuery({
		variables: { categoryId: categoryFilter },
	});

	const { data: categoriesData } = useGetAllWorkoutCategoriesQuery();

	const [createWorkout, { loading: creating }] = useCreateWorkoutMutation({
		onCompleted: () => {
			notifications.show({
				title: 'Success',
				message: 'Workout created successfully',
				color: 'green',
			});
			close();
			refetch();
		},
		onError: (error: Error) => {
			notifications.show({
				title: 'Error',
				message: error.message,
				color: 'red',
			});
		},
	});

	const [updateWorkout, { loading: updating }] = useUpdateWorkoutMutation({
		onCompleted: () => {
			notifications.show({
				title: 'Success',
				message: 'Workout updated successfully',
				color: 'green',
			});
			close();
			refetch();
		},
		onError: (error: Error) => {
			notifications.show({
				title: 'Error',
				message: error.message,
				color: 'red',
			});
		},
	});

	const [deleteWorkout, { loading: deleting }] = useDeleteWorkoutMutation({
		onCompleted: () => {
			notifications.show({
				title: 'Success',
				message: 'Workout deleted successfully',
				color: 'green',
			});
			refetch();
		},
		onError: (error: Error) => {
			notifications.show({
				title: 'Error',
				message: error.message,
				color: 'red',
			});
		},
	});

	const [bulkUpdateSortOrder, { loading: reordering }] = useBulkUpdateWorkoutSortOrderMutation({
		onCompleted: () => {
			notifications.show({
				title: 'Success',
				message: 'Workout order updated successfully',
				color: 'green',
			});
			closeReorder();
			refetch();
		},
		onError: (error: Error) => {
			notifications.show({
				title: 'Error',
				message: error.message,
				color: 'red',
			});
		},
	});

	const handleOpenCreate = () => {
		setSelectedWorkout(null);
		open();
	};

	const handleOpenEdit = (workout: any) => {
		console.log('[handleOpenEdit] Raw workout from GraphQL:', workout);
		// Deep clone the workout to make it mutable (GraphQL responses are frozen)
		const clonedWorkout = JSON.parse(JSON.stringify(workout));

		// Use translations from API if available, otherwise create default structure
		const workoutTranslations = clonedWorkout.translations || [];

		const formData = {
			id: clonedWorkout.id, // Include the ID for updates
			categoryId: clonedWorkout.categoryId,
			type: clonedWorkout.type || 'REFLECTION_INPUT',
			intensity: clonedWorkout.intensity,
			frequency: clonedWorkout.frequency,
			frequencyPeriod: clonedWorkout.frequencyPeriod,
			durationMin: clonedWorkout.durationMin,
			durationMax: clonedWorkout.durationMax,
			isPremium: clonedWorkout.isPremium || false,
			translations:
				workoutTranslations.length > 0
					? workoutTranslations
					: [
							{
								language: 'en',
								name: clonedWorkout.name,
								why: clonedWorkout.why,
								about: clonedWorkout.about,
								instructions: clonedWorkout.instructions,
								thankYouMessage: clonedWorkout.thankYouMessage,
								reflectionMessage: clonedWorkout.reflectionMessage,
							},
							{
								language: 'da',
								name: '',
								why: '',
								about: '',
								instructions: '',
								thankYouMessage: '',
								reflectionMessage: '',
							},
						],
			exercises: clonedWorkout.exercises.map((exercise: any) => ({
				translations:
					exercise.translations && exercise.translations.length > 0
						? exercise.translations
						: [
								{ language: 'en', name: exercise.name, tips: exercise.tips },
								{ language: 'da', name: '', tips: [] },
							],
				sortOrder: exercise.sortOrder,
				groupId: exercise.groupId !== undefined ? exercise.groupId : null,
				durationMin: exercise.durationMin,
				durationMax: exercise.durationMax,
				duration: exercise.duration,
				typeData: exercise.typeData,
			})),
		};
		console.log('[handleOpenEdit] Transformed formData:', formData);
		console.log('[handleOpenEdit] First exercise typeData:', formData.exercises[0]?.typeData);
		setSelectedWorkout(formData);
		open();
	};

	const handleSubmit = async (values: any) => {
		try {
			// Check if we're editing (has id) or creating (no id)
			if (selectedWorkout?.id) {
				// Update existing workout
				const { id, ...input } = values;
				await updateWorkout({
					variables: {
						id: selectedWorkout.id,
						input,
					},
				});
			} else {
				// Create new workout
				await createWorkout({
					variables: {
						input: values,
					},
				});
			}
		} catch (error) {
			console.error('Error saving workout:', error);
		}
	};

	const handleDelete = async (workoutId: string) => {
		modals.openConfirmModal({
			title: 'Delete Workout',
			centered: true,
			children: (
				<Text size="sm">
					Are you sure you want to delete this workout? This action cannot be undone.
				</Text>
			),
			labels: { confirm: 'Delete', cancel: 'Cancel' },
			confirmProps: { color: 'red' },
			onConfirm: async () => {
				try {
					await deleteWorkout({
						variables: { id: workoutId },
					});
				} catch (error) {
					console.error('Delete error:', error);
				}
			},
		});
	};

	const handleSaveOrder = async (orderedWorkouts: Array<{ id: string; sortOrder: number; categoryId: string }>) => {
		try {
			await bulkUpdateSortOrder({
				variables: {
					input: orderedWorkouts.map(({ id, sortOrder }) => ({ id, sortOrder })),
				},
			});
		} catch (error) {
			console.error('Reorder error:', error);
		}
	};

	useEffect(() => {
		if(categoriesData?.getAllWorkoutCategories?.length && categoriesData?.getAllWorkoutCategories?.length > 0) {
			setCategoryFilter(categoriesData?.getAllWorkoutCategories[0].id);
		}
	}, [categoriesData]);

	const workouts = data?.getAllWorkouts || [];

	return (
		<ScrollContainer>
			<Stack gap="lg">
				<Group justify="space-between">
					<div>
						<Text size="lg" fw={600}>
							Workouts
						</Text>
						<Text size="sm" c="dimmed">
							Manage workout programs and exercises
						</Text>
					</div>
					<Group>
						<Select
							placeholder="Filter by category"
							clearable
							data={
								categoriesData?.getAllWorkoutCategories
									.filter((cat) => cat.isActive)
									.map((cat) => ({
										value: cat.id,
										label: cat.name,
									})) || []
							}
							value={categoryFilter}
							onChange={setCategoryFilter}
							w={250}
						/>
						<Button
							leftSection={<IconArrowsSort size={16} />}
							onClick={openReorder}
							variant="light"
						>
							Reorder Workouts
						</Button>
						<Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
							Create Workout
						</Button>
					</Group>
				</Group>

				<DataContainer loading={loading} error={undefined}>
					<WorkoutsList
						workouts={workouts}
						onEdit={handleOpenEdit}
						onDelete={handleDelete}
						loading={loading || deleting}
					/>
				</DataContainer>
			</Stack>

			<Modal
				opened={opened}
				onClose={close}
				size="xl"
				title={selectedWorkout ? 'Edit Workout' : 'Create Workout'}
				styles={{
					body: {
						padding: 0,
						maxHeight: 'calc(90vh - 80px)',
						display: 'flex',
						flexDirection: 'column',
					},
				}}
			>
				<WorkoutForm
					initialValues={selectedWorkout}
					onSubmit={handleSubmit}
					onCancel={close}
					loading={creating || updating}
				/>
			</Modal>

			<WorkoutReorderModal
				opened={reorderOpened}
				onClose={closeReorder}
				onSave={handleSaveOrder}
			/>
		</ScrollContainer>
	);
}
